import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Stack } from '@chakra-ui/react';
import { Typography } from '@shinetools/sunshine-universal';
import {
  addDays,
  endOfMonth,
  getDate,
  getDaysInMonth,
  startOfMonth,
} from 'date-fns';

import { ShineRecurringAccountingExportSettingsCadence } from '__generated__/GQL';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';

import { AccountingExportSchema } from '../../accountingExport.schema';
import locales from './locales';

const CADENCE_OPTIONS = [
  {
    label:
      locales.cadence[ShineRecurringAccountingExportSettingsCadence.Monthly],
    value: ShineRecurringAccountingExportSettingsCadence.Monthly,
  },
] satisfies {
  label: string;
  value: ShineRecurringAccountingExportSettingsCadence;
}[];

export const Frequency = () => {
  const { control, watch } = useFormContext<AccountingExportSchema>();

  const cadenceOffset = watch('cadenceOffset');

  // Safety check to ensure we never show that exports are sent on the 32nd or later
  const sentOnDate = cadenceOffset > 30 ? 31 : cadenceOffset + 1;

  const datePickerValue = useMemo(() => {
    const startOfCurrentMonth = startOfMonth(new Date());
    const maxOffsetInMonth = getDaysInMonth(startOfCurrentMonth) - 1;

    // Ensures we don't adds more days that are in the current month
    const sanitizedOffset =
      cadenceOffset > maxOffsetInMonth ? maxOffsetInMonth : cadenceOffset;

    return addDays(startOfCurrentMonth, sanitizedOffset);
  }, [cadenceOffset]);

  return (
    <SunshineCard>
      <Stack>
        <Typography.Text bold size="large">
          {locales.title}
        </Typography.Text>

        <Typography.Text variant="secondary">
          {locales.formatString(locales.description, {
            date: sentOnDate,
          })}
        </Typography.Text>

        <Stack align="center" direction="row" marginTop="space-16">
          <Typography.Text variant="secondary" whiteSpace="nowrap">
            {locales.sendOn}
          </Typography.Text>

          <Controller
            control={control}
            name="cadenceOffset"
            render={({ field: { onChange } }) => (
              <Form.Field flexGrow={1}>
                <Form.SunshineInputDatePicker
                  modifiers={{
                    disabled: {
                      after: endOfMonth(new Date()),
                      before: startOfMonth(new Date()),
                    },
                  }}
                  onChange={(date) => {
                    if (date) {
                      // We subtract one as we store the offset, not the date number
                      onChange(getDate(date) - 1);
                    }
                  }}
                  value={datePickerValue}
                />
              </Form.Field>
            )}
          />

          <Typography.Text variant="secondary" whiteSpace="nowrap">
            {locales.each}
          </Typography.Text>

          <Controller
            control={control}
            name="cadence"
            render={({ field: { value } }) => {
              const selectedOption = CADENCE_OPTIONS.find(
                (option) => option.value === value,
              );

              return (
                <Form.Field>
                  <Form.SunshineSelect
                    isDisabled
                    options={CADENCE_OPTIONS}
                    value={selectedOption}
                  />
                </Form.Field>
              );
            }}
          />
        </Stack>
      </Stack>
    </SunshineCard>
  );
};
